import React, { useState, useEffect, useCallback } from 'react';
import { syncDateRange } from '../utils/dateUtils';
import useIsMobile from '../hooks/useIsMobile';

function BanquetCompetitorMaster({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [competitors, setCompetitors] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [activeTab, setActiveTab] = useState('competitors'); // competitors | rates | comparison
  const [editingComp, setEditingComp] = useState(null);
  const [editingRate, setEditingRate] = useState(null);
  const [showCompForm, setShowCompForm] = useState(false);
  const [showRateForm, setShowRateForm] = useState(false);

  // Google Places search
  const [showGoogleSearch, setShowGoogleSearch] = useState(false);
  const [googleQuery, setGoogleQuery] = useState('');
  const [googleResults, setGoogleResults] = useState([]);
  const [googleSearching, setGoogleSearching] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const [syncing, setSyncing] = useState({}); // { competitorId: true }
  const [syncAllLoading, setSyncAllLoading] = useState(false);

  // Reviews panel
  const [reviewsComp, setReviewsComp] = useState(null); // competitor obj
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // PredictHQ AI Predictions
  const [phqInsights, setPhqInsights] = useState(null);
  const [phqLoading, setPhqLoading] = useState(false);
  const [phqExpanded, setPhqExpanded] = useState(null);

  // Social Media
  const [socialFeed, setSocialFeed] = useState(null);
  const [socialLoading, setSocialLoading] = useState(false);
  const [socialSyncing, setSocialSyncing] = useState({});  // { competitorId: true }
  const [socialSyncAllLoading, setSocialSyncAllLoading] = useState(false);
  const [socialPlatformFilter, setSocialPlatformFilter] = useState('');
  const [socialCompFilter, setSocialCompFilter] = useState('');

  // Google Trends
  const [trendsData, setTrendsData] = useState(null);
  const [trendsFetching, setTrendsFetching] = useState(false);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsMonths, setTrendsMonths] = useState(12);
  const [trendsCompareKws, setTrendsCompareKws] = useState('');
  const [trendsCompareData, setTrendsCompareData] = useState(null);
  const [trendsComparing, setTrendsComparing] = useState(false);

  // Competitor form
  const emptyComp = { competitor_name: '', address: '', city: '', hall_count: '', max_capacity: '', price_range: '', instagram_handle: '', facebook_page_url: '', notes: '', is_own_property: false };
  const [compForm, setCompForm] = useState(emptyComp);

  // Rate form
  const emptyRate = { competitor_id: '', is_own_hotel: false, from_date: '', to_date: '', event_type: 'GENERAL', rate_type: 'PER_PLATE', rate_veg: '', rate_nonveg: '', min_guests: '', package_details: '', source: '', notes: '' };
  const [rateForm, setRateForm] = useState(emptyRate);

  // Comparison
  const [compYear, setCompYear] = useState(new Date().getFullYear());
  const [compEventType, setCompEventType] = useState('GENERAL');
  const [compData, setCompData] = useState(null);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const loadCompetitors = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/banquet-intelligence/competitors', { headers: headers() });
      if (res.ok) setCompetitors(await res.json());
    } catch (_) {}
  }, [headers]);

  const loadRates = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/banquet-intelligence/competitor-rates', { headers: headers() });
      if (res.ok) setRates(await res.json());
    } catch (_) {}
  }, [headers]);

  const loadComparison = useCallback(async () => {
    try {
      const res = await fetch(`/api/v1/banquet-intelligence/competitor-rates/comparison?year=${compYear}&event_type=${compEventType}`, { headers: headers() });
      if (res.ok) setCompData(await res.json());
    } catch (_) {}
  }, [headers, compYear, compEventType]);

  useEffect(() => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    Promise.all([loadCompetitors(), loadRates()]).finally(() => setLoading(false));
  }, [selectedProperty?.id, loadCompetitors, loadRates]);

  useEffect(() => {
    if (activeTab === 'comparison') loadComparison();
  }, [activeTab, loadComparison]);

  const saveCompetitor = async () => {
    setError(''); setMsg('');
    if (!compForm.competitor_name.trim()) return setError('Competitor name is required');
    try {
      const url = editingComp ? `/api/v1/banquet-intelligence/competitors/${editingComp.id}` : '/api/v1/banquet-intelligence/competitors';
      const method = editingComp ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(compForm) });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed');
      setMsg(editingComp ? 'Competitor updated' : 'Competitor added');
      setShowCompForm(false); setEditingComp(null); setCompForm(emptyComp);
      loadCompetitors();
    } catch (err) { setError(err.message); }
  };

  const deleteCompetitor = async (id) => {
    if (!window.confirm('Delete this competitor?')) return;
    try {
      await fetch(`/api/v1/banquet-intelligence/competitors/${id}`, { method: 'DELETE', headers: headers() });
      loadCompetitors();
    } catch (_) {}
  };

  const saveRate = async () => {
    setError(''); setMsg('');
    if (!rateForm.from_date || !rateForm.to_date) return setError('From and To dates are required');
    if (!rateForm.is_own_hotel && !rateForm.competitor_id) return setError('Select a competitor or mark as Own Hotel');
    try {
      const url = editingRate ? `/api/v1/banquet-intelligence/competitor-rates/${editingRate.id}` : '/api/v1/banquet-intelligence/competitor-rates';
      const method = editingRate ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(rateForm) });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed');
      setMsg(editingRate ? 'Rate updated' : 'Rate added');
      setShowRateForm(false); setEditingRate(null); setRateForm(emptyRate);
      loadRates();
    } catch (err) { setError(err.message); }
  };

  const deleteRate = async (id) => {
    if (!window.confirm('Delete this rate entry?')) return;
    try {
      await fetch(`/api/v1/banquet-intelligence/competitor-rates/${id}`, { method: 'DELETE', headers: headers() });
      loadRates();
    } catch (_) {}
  };

  // ── Google Places Search ──
  const searchGoogle = async () => {
    if (!googleQuery.trim()) return;
    setGoogleSearching(true); setGoogleError(''); setGoogleResults([]);
    try {
      const res = await fetch('/api/v1/banquet-intelligence/google-search', {
        method: 'POST', headers: headers(), body: JSON.stringify({ query: googleQuery }),
      });
      const data = await res.json();
      if (!res.ok) return setGoogleError(data.error || 'Search failed');
      setGoogleResults(data.results || []);
      if (!data.results?.length) setGoogleError('No results found. Try a different search term.');
    } catch (err) { setGoogleError(err.message); }
    finally { setGoogleSearching(false); }
  };

  const importFromGoogle = async (place) => {
    setError(''); setMsg('');
    try {
      const res = await fetch('/api/v1/banquet-intelligence/competitors/import-from-google', {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          place_id: place.place_id, name: place.name, address: place.address,
          latitude: place.latitude, longitude: place.longitude,
          rating: place.rating, review_count: place.review_count,
        }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Import failed');
      setMsg(`"${place.name}" imported as competitor`);
      setGoogleResults(prev => prev.filter(p => p.place_id !== place.place_id));
      loadCompetitors();
    } catch (err) { setError(err.message); }
  };

  // ── Google Sync ──
  const syncCompetitor = async (comp) => {
    setSyncing(prev => ({ ...prev, [comp.id]: true }));
    setError(''); setMsg('');
    try {
      const res = await fetch(`/api/v1/banquet-intelligence/competitors/${comp.id}/google-sync`, {
        method: 'POST', headers: headers(), body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Sync failed');
      setMsg(`"${comp.competitor_name}" synced — Rating: ${data.competitor.google_rating}, Reviews: ${data.reviews_synced}`);
      loadCompetitors();
    } catch (err) { setError(err.message); }
    finally { setSyncing(prev => ({ ...prev, [comp.id]: false })); }
  };

  const syncAll = async () => {
    setSyncAllLoading(true); setError(''); setMsg('');
    try {
      const res = await fetch('/api/v1/banquet-intelligence/competitors/google-sync-all', {
        method: 'POST', headers: headers(), body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Sync failed');
      setMsg(`Synced ${data.synced}/${data.total} competitors from Google`);
      loadCompetitors();
    } catch (err) { setError(err.message); }
    finally { setSyncAllLoading(false); }
  };

  // ── Reviews ──
  const loadReviews = async (comp) => {
    setReviewsComp(comp); setReviewsLoading(true); setReviews([]);
    try {
      const res = await fetch(`/api/v1/banquet-intelligence/competitors/${comp.id}/reviews`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setReviews(data.reviews || []);
      }
    } catch (_) {}
    finally { setReviewsLoading(false); }
  };

  // ── Google Trends ──
  const loadTrends = useCallback(async () => {
    setTrendsLoading(true);
    try {
      const res = await fetch(`/api/v1/banquet-intelligence/trends?months=${trendsMonths}`, { headers: headers() });
      if (res.ok) setTrendsData(await res.json());
    } catch (_) {}
    finally { setTrendsLoading(false); }
  }, [headers, trendsMonths]);

  const fetchTrends = async () => {
    setTrendsFetching(true); setError(''); setMsg('');
    try {
      const res = await fetch('/api/v1/banquet-intelligence/trends/fetch', {
        method: 'POST', headers: headers(), body: JSON.stringify({ months: trendsMonths }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Trends fetch failed');
      setMsg(`Fetched ${data.terms_fetched}/${data.total_terms} search terms from Google Trends`);
      loadTrends();
    } catch (err) { setError(err.message); }
    finally { setTrendsFetching(false); }
  };

  const compareTrends = async () => {
    const kws = trendsCompareKws.split('\n').map(k => k.trim()).filter(Boolean);
    if (!kws.length) return setError('Enter keywords to compare (one per line)');
    if (kws.length > 5) return setError('Max 5 keywords for comparison');
    setTrendsComparing(true); setTrendsCompareData(null); setError('');
    try {
      const res = await fetch('/api/v1/banquet-intelligence/trends/compare', {
        method: 'POST', headers: headers(), body: JSON.stringify({ keywords: kws, months: trendsMonths }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Compare failed');
      setTrendsCompareData(data);
    } catch (err) { setError(err.message); }
    finally { setTrendsComparing(false); }
  };

  useEffect(() => {
    if (activeTab === 'trends' && selectedProperty?.id) loadTrends();
  }, [activeTab, selectedProperty?.id, loadTrends]);

  // ── Social Media ──
  const loadSocialFeed = useCallback(async () => {
    setSocialLoading(true);
    try {
      let url = '/api/v1/banquet-intelligence/social/feed?limit=100';
      if (socialPlatformFilter) url += `&platform=${socialPlatformFilter}`;
      if (socialCompFilter) url += `&competitor_id=${socialCompFilter}`;
      const res = await fetch(url, { headers: headers() });
      if (res.ok) setSocialFeed(await res.json());
    } catch (_) {}
    finally { setSocialLoading(false); }
  }, [headers, socialPlatformFilter, socialCompFilter]);

  const syncSocial = async (compId) => {
    setSocialSyncing(prev => ({ ...prev, [compId]: true }));
    setError(''); setMsg('');
    try {
      const res = await fetch(`/api/v1/banquet-intelligence/social/sync/${compId}`, {
        method: 'POST', headers: headers(), body: '{}',
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Social sync failed');
      const parts = [];
      if (data.instagram_posts) parts.push(`${data.instagram_posts} IG posts`);
      if (data.facebook_posts) parts.push(`${data.facebook_posts} FB posts`);
      if (data.instagram_error) parts.push(`IG error: ${data.instagram_error}`);
      if (data.facebook_error) parts.push(`FB error: ${data.facebook_error}`);
      setMsg(`${data.competitor_name}: ${parts.join(', ') || 'No posts found'}`);
      loadSocialFeed();
    } catch (err) { setError(err.message); }
    finally { setSocialSyncing(prev => ({ ...prev, [compId]: false })); }
  };

  const syncAllSocial = async () => {
    setSocialSyncAllLoading(true); setError(''); setMsg('');
    try {
      const res = await fetch('/api/v1/banquet-intelligence/social/sync-all', {
        method: 'POST', headers: headers(), body: '{}',
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Social sync failed');
      const totalPosts = data.details?.reduce((s, d) => s + (d.instagram || 0) + (d.facebook || 0), 0) || 0;
      setMsg(`Synced ${data.synced}/${data.total} competitors — ${totalPosts} posts fetched`);
      loadSocialFeed();
    } catch (err) { setError(err.message); }
    finally { setSocialSyncAllLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'social' && selectedProperty?.id) loadSocialFeed();
  }, [activeTab, selectedProperty?.id, loadSocialFeed]);

  // PredictHQ AI Predictions
  const loadPhqInsights = useCallback(async () => {
    setPhqLoading(true);
    try {
      const q = new URLSearchParams();
      q.set('from', new Date().toISOString().slice(0, 10));
      const res = await fetch(`/api/v1/event-calendar/insights?${q.toString()}`, { headers: headers() });
      if (res.ok) setPhqInsights(await res.json());
    } catch {}
    finally { setPhqLoading(false); }
  }, [headers]);

  useEffect(() => {
    if (activeTab === 'ai-predictions') loadPhqInsights();
  }, [activeTab, loadPhqInsights]);

  if (!selectedProperty?.id) return <div className="page-shell"><p style={{ color: '#94a3b8' }}>Please select a property.</p></div>;

  const tabBtn = (key, label) => (
    <button key={key} onClick={() => setActiveTab(key)}
      style={{ padding: '0.55rem 1rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600,
        background: activeTab === key ? 'var(--theme-primary-dark)' : '#e5e7eb', color: activeTab === key ? '#fff' : '#374151' }}>
      {label}
    </button>
  );

  const inputStyle = { width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box' };
  const labelStyle = { fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 3, display: 'block' };

  return (
    <div className="page-shell">
      <h3 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.3rem' }}>Banquet Intelligence — Competitor & Rate Tracker</h3>
      <p style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: '1rem' }}>Manage competitors, track rates, and view 365-day comparisons.</p>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {tabBtn('competitors', 'Competitors')}
        {tabBtn('rates', 'Rate Tracker')}
        {tabBtn('comparison', '365-Day Comparison')}
        {tabBtn('trends', 'Market Trends')}
        {tabBtn('social', 'Social Media')}
        {tabBtn('ai-predictions', 'AI Predictions')}
      </div>

      {error && <div className="error-banner" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      {msg && <div style={{ padding: '0.5rem 1rem', background: '#dcfce7', color: '#166534', borderRadius: 8, marginBottom: '0.75rem', fontSize: '0.85rem' }}>{msg}</div>}
      {loading && <div style={{ color: '#6b7280', marginBottom: '0.5rem' }}>Loading...</div>}

      {/* ═══ COMPETITORS TAB ═══ */}
      {activeTab === 'competitors' && (
        <>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <button onClick={() => { setShowCompForm(true); setEditingComp(null); setCompForm(emptyComp); setShowGoogleSearch(false); }}
              className="btn-success" style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
              + Add Manually
            </button>
            <button onClick={() => { setShowGoogleSearch(!showGoogleSearch); setShowCompForm(false); }}
              style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600,
                background: showGoogleSearch ? '#3b82f6' : '#1e40af', color: '#fff' }}>
              Search Google Places
            </button>
            {competitors.some(c => c.google_place_id) && (
              <button onClick={syncAll} disabled={syncAllLoading}
                style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, cursor: syncAllLoading ? 'wait' : 'pointer',
                  fontWeight: 600, background: '#fff', color: '#1e40af', marginLeft: 'auto' }}>
                {syncAllLoading ? 'Syncing All...' : 'Sync All from Google'}
              </button>
            )}
          </div>

          {/* Google Places Search Panel */}
          {showGoogleSearch && (
            <div className="card" style={{ padding: '1.2rem', marginBottom: '1rem', borderLeft: '4px solid #1e40af' }}>
              <div style={{ fontWeight: 700, marginBottom: '0.5rem', color: '#1e40af' }}>Search Google Places</div>
              <p style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                Search for banquet halls, wedding venues, or competitor hotels near you. Results come directly from Google Maps.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <input value={googleQuery} onChange={e => setGoogleQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchGoogle()}
                  placeholder="e.g. banquet halls in Chennai, wedding venues near me"
                  style={{ ...inputStyle, flex: 1 }} />
                <button onClick={searchGoogle} disabled={googleSearching}
                  style={{ padding: '0.5rem 1.2rem', border: 'none', borderRadius: 6, cursor: googleSearching ? 'wait' : 'pointer',
                    fontWeight: 600, background: '#1e40af', color: '#fff', whiteSpace: 'nowrap' }}>
                  {googleSearching ? 'Searching...' : 'Search'}
                </button>
              </div>
              {googleError && <div style={{ color: '#991b1b', fontSize: '0.82rem', marginBottom: '0.5rem' }}>{googleError}</div>}

              {googleResults.length > 0 && (
                <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                  {googleResults.map((p, i) => {
                    const alreadyAdded = competitors.some(c => c.google_place_id === p.place_id);
                    return (
                      <div key={p.place_id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                        padding: '0.6rem 0.8rem', borderBottom: '1px solid #f3f4f6', gap: '0.75rem' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{p.name}</div>
                          <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{p.address}</div>
                          <div style={{ fontSize: '0.78rem', color: '#475569', marginTop: 2 }}>
                            {p.rating && <span style={{ fontWeight: 600, color: '#d97706' }}>{p.rating} ★</span>}
                            {p.review_count > 0 && <span style={{ marginLeft: 6, color: '#6b7280' }}>({p.review_count} reviews)</span>}
                            {p.price_level && <span style={{ marginLeft: 8, color: '#059669' }}>{p.price_level}</span>}
                          </div>
                        </div>
                        <button onClick={() => importFromGoogle(p)} disabled={alreadyAdded}
                          style={{ padding: '0.35rem 0.8rem', border: 'none', borderRadius: 6, cursor: alreadyAdded ? 'default' : 'pointer',
                            fontWeight: 600, fontSize: '0.78rem', whiteSpace: 'nowrap',
                            background: alreadyAdded ? '#e5e7eb' : '#059669', color: alreadyAdded ? '#9ca3af' : '#fff' }}>
                          {alreadyAdded ? 'Already Added' : '+ Import'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {showCompForm && (
            <div className="card" style={{ padding: '1.2rem', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 700, marginBottom: '0.75rem', color: 'var(--theme-primary-dark)' }}>{editingComp ? 'Edit Competitor' : 'Add Competitor'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                <div><label style={labelStyle}>Name *</label><input value={compForm.competitor_name} onChange={e => setCompForm({ ...compForm, competitor_name: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>City</label><input value={compForm.city} onChange={e => setCompForm({ ...compForm, city: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>Address</label><input value={compForm.address} onChange={e => setCompForm({ ...compForm, address: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>Hall Count</label><input type="number" value={compForm.hall_count} onChange={e => setCompForm({ ...compForm, hall_count: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>Max Capacity</label><input type="number" value={compForm.max_capacity} onChange={e => setCompForm({ ...compForm, max_capacity: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>Price Range</label><input value={compForm.price_range} onChange={e => setCompForm({ ...compForm, price_range: e.target.value })} placeholder="e.g. 800-1500" style={inputStyle} /></div>
                <div><label style={labelStyle}>Instagram</label><input value={compForm.instagram_handle} onChange={e => setCompForm({ ...compForm, instagram_handle: e.target.value })} placeholder="@handle" style={inputStyle} /></div>
                <div><label style={labelStyle}>Facebook URL</label><input value={compForm.facebook_page_url} onChange={e => setCompForm({ ...compForm, facebook_page_url: e.target.value })} style={inputStyle} /></div>
              </div>
              <div style={{ marginTop: '0.5rem' }}><label style={labelStyle}>Notes</label><textarea value={compForm.notes} onChange={e => setCompForm({ ...compForm, notes: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input type="checkbox" id="is_own_property" checked={!!compForm.is_own_property} onChange={e => setCompForm({ ...compForm, is_own_property: e.target.checked })} />
                <label htmlFor="is_own_property" style={{ fontSize: '0.85rem', color: '#374151', cursor: 'pointer' }}>
                  This is my property (will appear in Google Reviews but excluded from competitor comparison grid)
                </label>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                <button onClick={saveCompetitor} className="btn-success" style={{ padding: '0.45rem 1rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Save</button>
                <button onClick={() => { setShowCompForm(false); setEditingComp(null); }} style={{ padding: '0.45rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', background: '#fff' }}>Cancel</button>
              </div>
            </div>
          )}

          <div className="card" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>City</th>
                  <th style={{ padding: '0.6rem', textAlign: 'right' }}>Halls</th>
                  <th style={{ padding: '0.6rem', textAlign: 'right' }}>Max Cap</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Price Range</th>
                  <th style={{ padding: '0.6rem', textAlign: 'center' }}>Google Rating</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Instagram</th>
                  <th style={{ padding: '0.6rem', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.6rem', fontWeight: 600 }}>
                      {c.competitor_name}
                      {c.is_own_property && <span style={{ marginLeft: 6, fontSize: '0.68rem', background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 4, padding: '1px 5px', fontWeight: 600 }}>My Property</span>}
                      {c.google_place_id && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#1e40af', verticalAlign: 'super' }} title="Linked to Google Places">G</span>}
                    </td>
                    <td style={{ padding: '0.6rem' }}>{c.city || '-'}</td>
                    <td style={{ padding: '0.6rem', textAlign: 'right' }}>{c.hall_count || '-'}</td>
                    <td style={{ padding: '0.6rem', textAlign: 'right' }}>{c.max_capacity || '-'}</td>
                    <td style={{ padding: '0.6rem' }}>{c.price_range || '-'}</td>
                    <td style={{ padding: '0.6rem', textAlign: 'center' }}>
                      {c.google_rating ? (
                        <span style={{ cursor: 'pointer', color: '#d97706', fontWeight: 600 }} onClick={() => loadReviews(c)}
                          title="Click to view reviews">
                          {Number(c.google_rating).toFixed(1)} ★ <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.78rem' }}>({c.google_review_count})</span>
                        </span>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '0.6rem' }}>{c.instagram_handle || '-'}</td>
                    <td style={{ padding: '0.6rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {c.google_place_id && (
                        <button onClick={() => syncCompetitor(c)} disabled={syncing[c.id]}
                          title="Sync rating & reviews from Google"
                          style={{ padding: '0.25rem 0.5rem', border: '1px solid #93c5fd', borderRadius: 4, cursor: syncing[c.id] ? 'wait' : 'pointer',
                            fontSize: '0.72rem', marginRight: 4, background: '#eff6ff', color: '#1e40af', fontWeight: 600 }}>
                          {syncing[c.id] ? '...' : 'Sync'}
                        </button>
                      )}
                      {c.google_rating && (
                        <button onClick={() => loadReviews(c)}
                          title="View Google reviews"
                          style={{ padding: '0.25rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer',
                            fontSize: '0.72rem', marginRight: 4, background: '#fff' }}>
                          Reviews
                        </button>
                      )}
                      <button onClick={() => { setEditingComp(c); setCompForm(c); setShowCompForm(true); setShowGoogleSearch(false); }}
                        style={{ padding: '0.25rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem', marginRight: 4 }}>Edit</button>
                      <button onClick={() => deleteCompetitor(c.id)}
                        style={{ padding: '0.25rem 0.6rem', border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem', color: '#991b1b', background: '#fef2f2' }}>Del</button>
                    </td>
                  </tr>
                ))}
                {!competitors.length && <tr><td colSpan={8} style={{ padding: '1rem', color: '#94a3b8', textAlign: 'center' }}>No competitors added yet. Use "Search Google Places" to find and import competitors.</td></tr>}
              </tbody>
            </table>
          </div>

          {/* Reviews Panel */}
          {reviewsComp && (
            <div className="card" style={{ padding: '1.2rem', marginTop: '1rem', borderLeft: '4px solid #d97706' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <div>
                  <span style={{ fontWeight: 700, color: '#d97706' }}>Google Reviews — {reviewsComp.competitor_name}</span>
                  {reviewsComp.google_rating && (
                    <span style={{ marginLeft: 10, fontSize: '0.85rem', color: '#475569' }}>
                      {Number(reviewsComp.google_rating).toFixed(1)} ★ ({reviewsComp.google_review_count} total)
                    </span>
                  )}
                </div>
                <button onClick={() => setReviewsComp(null)}
                  style={{ padding: '0.25rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem', background: '#fff' }}>Close</button>
              </div>
              {reviewsLoading && <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>Loading reviews...</div>}
              {!reviewsLoading && reviews.length === 0 && (
                <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No reviews stored yet. Click "Sync" to fetch reviews from Google.</div>
              )}
              {reviews.map(rv => (
                <div key={rv.id} style={{ padding: '0.6rem 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{rv.reviewer_name}</span>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {rv.review_date ? new Date(rv.review_date).toLocaleDateString() : ''}
                    </span>
                  </div>
                  <div style={{ marginTop: 2 }}>
                    <span style={{ color: '#d97706', fontWeight: 600, fontSize: '0.82rem' }}>
                      {'★'.repeat(Math.round(rv.rating || 0))}{'☆'.repeat(5 - Math.round(rv.rating || 0))}
                    </span>
                    <span style={{ marginLeft: 8, fontSize: '0.72rem', padding: '1px 6px', borderRadius: 4,
                      background: rv.sentiment === 'POSITIVE' ? '#dcfce7' : rv.sentiment === 'NEGATIVE' ? '#fef2f2' : '#f3f4f6',
                      color: rv.sentiment === 'POSITIVE' ? '#166534' : rv.sentiment === 'NEGATIVE' ? '#991b1b' : '#6b7280' }}>
                      {rv.sentiment}
                    </span>
                  </div>
                  {rv.review_text && <div style={{ fontSize: '0.82rem', color: '#475569', marginTop: 4, lineHeight: 1.4 }}>{rv.review_text}</div>}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ═══ RATE TRACKER TAB ═══ */}
      {activeTab === 'rates' && (
        <>
          <button onClick={() => { setShowRateForm(true); setEditingRate(null); setRateForm(emptyRate); }}
            className="btn-success" style={{ marginBottom: '1rem', padding: '0.5rem 1rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
            + Add Rate Entry
          </button>

          {showRateForm && (
            <div className="card" style={{ padding: '1.2rem', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 700, marginBottom: '0.75rem', color: 'var(--theme-primary-dark)' }}>{editingRate ? 'Edit Rate' : 'Add Rate Entry'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                <div>
                  <label style={labelStyle}>
                    <input type="checkbox" checked={rateForm.is_own_hotel} onChange={e => setRateForm({ ...rateForm, is_own_hotel: e.target.checked, competitor_id: '' })} style={{ marginRight: 6 }} />
                    Own Hotel Rate
                  </label>
                </div>
                {!rateForm.is_own_hotel && (
                  <div><label style={labelStyle}>Competitor *</label>
                    <select value={rateForm.competitor_id} onChange={e => setRateForm({ ...rateForm, competitor_id: e.target.value })} style={inputStyle}>
                      <option value="">Select Competitor</option>
                      {competitors.map(c => <option key={c.id} value={c.id}>{c.competitor_name}</option>)}
                    </select>
                  </div>
                )}
                <div><label style={labelStyle}>From Date *</label><input type="date" value={rateForm.from_date} onChange={e => setRateForm(syncDateRange({ ...rateForm, from_date: e.target.value }, 'from_date', 'to_date', 'from_date'))} style={inputStyle} /></div>
                <div><label style={labelStyle}>To Date *</label><input type="date" value={rateForm.to_date} min={rateForm.from_date || undefined} onChange={e => setRateForm(syncDateRange({ ...rateForm, to_date: e.target.value }, 'from_date', 'to_date', 'to_date'))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Event Type</label>
                  <select value={rateForm.event_type} onChange={e => setRateForm({ ...rateForm, event_type: e.target.value })} style={inputStyle}>
                    {['GENERAL', 'WEDDING', 'CORPORATE', 'BIRTHDAY', 'SOCIAL'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Rate Type</label>
                  <select value={rateForm.rate_type} onChange={e => setRateForm({ ...rateForm, rate_type: e.target.value })} style={inputStyle}>
                    {['PER_PLATE', 'PER_EVENT', 'PER_ROOM_NIGHT'].map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Rate (Veg)</label><input type="number" value={rateForm.rate_veg} onChange={e => setRateForm({ ...rateForm, rate_veg: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>Rate (Non-Veg)</label><input type="number" value={rateForm.rate_nonveg} onChange={e => setRateForm({ ...rateForm, rate_nonveg: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>Min Guests</label><input type="number" value={rateForm.min_guests} onChange={e => setRateForm({ ...rateForm, min_guests: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>Source</label>
                  <select value={rateForm.source} onChange={e => setRateForm({ ...rateForm, source: e.target.value })} style={inputStyle}>
                    <option value="">Select</option>
                    {['Website', 'Phone Call', 'Walk-in', 'Agent', 'Other'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: '0.5rem' }}><label style={labelStyle}>Package Details / Notes</label><textarea value={rateForm.package_details || rateForm.notes} onChange={e => setRateForm({ ...rateForm, package_details: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                <button onClick={saveRate} className="btn-success" style={{ padding: '0.45rem 1rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Save</button>
                <button onClick={() => { setShowRateForm(false); setEditingRate(null); }} style={{ padding: '0.45rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', background: '#fff' }}>Cancel</button>
              </div>
            </div>
          )}

          <div className="card" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Hotel/Competitor</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>From</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>To</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Event Type</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Rate Type</th>
                  <th style={{ padding: '0.6rem', textAlign: 'right' }}>Veg</th>
                  <th style={{ padding: '0.6rem', textAlign: 'right' }}>Non-Veg</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Source</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Last Updated</th>
                  <th style={{ padding: '0.6rem', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rates.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #e5e7eb', background: r.is_own_hotel ? '#eff6ff' : undefined }}>
                    <td style={{ padding: '0.6rem', fontWeight: 600 }}>{r.is_own_hotel ? 'OWN HOTEL' : (r.competitor_name || '-')}</td>
                    <td style={{ padding: '0.6rem', fontSize: '0.82rem' }}>{r.from_date?.slice(0, 10)}</td>
                    <td style={{ padding: '0.6rem', fontSize: '0.82rem' }}>{r.to_date?.slice(0, 10)}</td>
                    <td style={{ padding: '0.6rem' }}>{r.event_type}</td>
                    <td style={{ padding: '0.6rem', fontSize: '0.82rem' }}>{(r.rate_type || '').replace(/_/g, ' ')}</td>
                    <td style={{ padding: '0.6rem', textAlign: 'right' }}>{r.rate_veg ? `₹${Number(r.rate_veg).toLocaleString()}` : '-'}</td>
                    <td style={{ padding: '0.6rem', textAlign: 'right' }}>{r.rate_nonveg ? `₹${Number(r.rate_nonveg).toLocaleString()}` : '-'}</td>
                    <td style={{ padding: '0.6rem', fontSize: '0.82rem' }}>{r.source || '-'}</td>
                    <td style={{ padding: '0.6rem', fontSize: '0.78rem', color: '#6b7280' }}>
                      {r.updated_at ? (() => {
                        const d = new Date(r.updated_at);
                        const diffDays = Math.floor((Date.now() - d) / 86400000);
                        if (diffDays === 0) return 'Today';
                        if (diffDays === 1) return 'Yesterday';
                        if (diffDays <= 7) return `${diffDays} days ago`;
                        return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                      })() : '-'}
                    </td>
                    <td style={{ padding: '0.6rem', textAlign: 'center' }}>
                      <button onClick={() => { setEditingRate(r); setRateForm(r); setShowRateForm(true); }} style={{ padding: '0.25rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem', marginRight: 4 }}>Edit</button>
                      <button onClick={() => deleteRate(r.id)} style={{ padding: '0.25rem 0.6rem', border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem', color: '#991b1b', background: '#fef2f2' }}>Del</button>
                    </td>
                  </tr>
                ))}
                {!rates.length && <tr><td colSpan={10} style={{ padding: '1rem', color: '#94a3b8', textAlign: 'center' }}>No rate entries yet</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══ 365-DAY COMPARISON TAB ═══ */}
      {activeTab === 'comparison' && (
        <>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>Year
              <select value={compYear} onChange={e => setCompYear(Number(e.target.value))} style={{ marginLeft: 6, padding: '0.4rem', borderRadius: 6, border: '1px solid #d1d5db' }}>
                {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
            <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>Event Type
              <select value={compEventType} onChange={e => setCompEventType(e.target.value)} style={{ marginLeft: 6, padding: '0.4rem', borderRadius: 6, border: '1px solid #d1d5db' }}>
                {['GENERAL', 'WEDDING', 'CORPORATE', 'BIRTHDAY', 'SOCIAL'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <button onClick={loadComparison} className="btn-success" style={{ padding: '0.4rem 1rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>Refresh</button>
          </div>

          {compData && (
            <div className="card" style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.78rem' }}>
                <thead>
                  <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left', position: 'sticky', top: 0, left: 0, background: 'var(--theme-primary-dark)', zIndex: 4, borderBottom: '2px solid #1e3a8a', whiteSpace: 'nowrap' }}>Date</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', position: 'sticky', top: 0, background: '#1e40af', color: '#fff', zIndex: 3, borderBottom: '2px solid #1e3a8a', whiteSpace: 'nowrap' }}>Own Hotel</th>
                    {compData.competitors.map(c => (
                      <th key={c.id} style={{ padding: '0.5rem', textAlign: 'right', position: 'sticky', top: 0, background: 'var(--theme-primary-dark)', zIndex: 3, borderBottom: '2px solid #1e3a8a', whiteSpace: 'nowrap' }}>{c.competitor_name}</th>
                    ))}
                    <th style={{ padding: '0.5rem', textAlign: 'right', position: 'sticky', top: 0, background: '#065f46', color: '#fff', zIndex: 3, borderBottom: '2px solid #064e3b', whiteSpace: 'nowrap' }}>Market Avg</th>
                  </tr>
                </thead>
                <tbody>
                  {(compData.days || []).map((day, i) => {
                    const ownRate = day.own_rate_veg;
                    const mktAvg = day.market_avg;
                    const ownColor = !ownRate || !mktAvg ? undefined : ownRate < mktAvg ? '#dcfce7' : ownRate > mktAvg ? '#fef2f2' : '#fefce8';
                    return (
                      <tr key={day.date} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ padding: '0.4rem 0.5rem', fontWeight: 500, position: 'sticky', left: 0, background: i % 2 === 0 ? '#fff' : '#fafafa', zIndex: 1 }}>
                          {day.date}
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontWeight: 700, background: ownColor }}>
                          {ownRate ? `₹${ownRate.toLocaleString()}` : '-'}
                        </td>
                        {compData.competitors.map(c => (
                          <td key={c.id} style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>
                            {day.competitors[c.id]?.rate_veg ? `₹${day.competitors[c.id].rate_veg.toLocaleString()}` : '-'}
                          </td>
                        ))}
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>
                          {mktAvg ? `₹${mktAvg.toLocaleString()}` : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {compData && !compData.days?.length && <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No rate data for this year/event type. Add rates in the Rate Tracker tab.</div>}
          {!compData && <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>Loading comparison...</div>}

          <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}>
            Legend: <span style={{ background: '#dcfce7', padding: '2px 8px', borderRadius: 4 }}>Below Market</span>{' '}
            <span style={{ background: '#fefce8', padding: '2px 8px', borderRadius: 4 }}>At Market</span>{' '}
            <span style={{ background: '#fef2f2', padding: '2px 8px', borderRadius: 4 }}>Above Market</span>
          </div>
        </>
      )}

      {/* ═══ MARKET TRENDS TAB ═══ */}
      {activeTab === 'trends' && (
        <>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>Period
              <select value={trendsMonths} onChange={e => setTrendsMonths(Number(e.target.value))} style={{ marginLeft: 6, padding: '0.4rem', borderRadius: 6, border: '1px solid #d1d5db' }}>
                <option value={3}>3 Months</option>
                <option value={6}>6 Months</option>
                <option value={12}>12 Months</option>
              </select>
            </label>
            <button onClick={fetchTrends} disabled={trendsFetching}
              style={{ padding: '0.45rem 1rem', border: 'none', borderRadius: 6, cursor: trendsFetching ? 'wait' : 'pointer',
                fontWeight: 600, background: '#059669', color: '#fff' }}>
              {trendsFetching ? 'Fetching from Google...' : 'Fetch Latest Trends'}
            </button>
            <button onClick={loadTrends} disabled={trendsLoading}
              style={{ padding: '0.45rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', background: '#fff', fontWeight: 600 }}>
              Refresh
            </button>
            {trendsData?.last_fetched && (
              <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: 'auto' }}>
                Last synced: {new Date(trendsData.last_fetched).toLocaleString()}
              </span>
            )}
          </div>

          {trendsLoading && <div style={{ color: '#6b7280', marginBottom: '0.75rem' }}>Loading stored trends...</div>}

          {/* Trends Chart (CSS bar chart) */}
          {trendsData && trendsData.terms?.length > 0 && (
            <div className="card" style={{ padding: '1.2rem', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 700, color: '#059669', marginBottom: '0.75rem' }}>Search Interest Over Time (0-100 scale)</div>
              {trendsData.terms.map(term => {
                const points = trendsData.trends[term] || [];
                if (!points.length) return null;
                const maxVal = Math.max(...points.map(p => p.interest_value || 0), 1);
                // Show last 26 weeks max for readability
                const displayPoints = points.slice(-26);
                return (
                  <div key={term} style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.4rem' }}>{term}</div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 80 }}>
                      {displayPoints.map((p, i) => {
                        const val = p.interest_value || 0;
                        const pct = Math.max((val / 100) * 100, 2);
                        const barColor = val >= 70 ? '#059669' : val >= 40 ? '#d97706' : '#94a3b8';
                        return (
                          <div key={i} title={`${p.period_start}: ${val}`}
                            style={{ flex: 1, minWidth: 4, maxWidth: 20, height: `${pct}%`, background: barColor,
                              borderRadius: '2px 2px 0 0', cursor: 'pointer', transition: 'height 0.2s' }} />
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: '#94a3b8', marginTop: 2 }}>
                      <span>{displayPoints[0]?.period_start}</span>
                      <span>{displayPoints[displayPoints.length - 1]?.period_start}</span>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 4 }}>
                      Latest: <strong>{points[points.length - 1]?.interest_value || 0}</strong>/100
                      {points.length >= 2 && (() => {
                        const curr = points[points.length - 1]?.interest_value || 0;
                        const prev = points[points.length - 2]?.interest_value || 0;
                        const diff = prev > 0 ? Math.round(((curr - prev) / prev) * 100) : 0;
                        if (diff === 0) return <span style={{ marginLeft: 8, color: '#6b7280' }}>(no change)</span>;
                        return <span style={{ marginLeft: 8, color: diff > 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                          {diff > 0 ? '+' : ''}{diff}% vs prev week
                        </span>;
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {trendsData && !trendsData.terms?.length && (
            <div className="card" style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
              No trends data yet. Configure search terms in <strong>Intelligence Config</strong>, then click <strong>Fetch Latest Trends</strong>.
            </div>
          )}

          {/* Related Queries */}
          {trendsData?.related_queries && (trendsData.related_queries.top?.length > 0 || trendsData.related_queries.rising?.length > 0) && (
            <div className="card" style={{ padding: '1.2rem', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 700, color: '#059669', marginBottom: '0.75rem' }}>Related Search Queries</div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
                {trendsData.related_queries.top?.length > 0 && (
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#475569', marginBottom: '0.4rem' }}>Top Queries</div>
                    {trendsData.related_queries.top.map((q, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem' }}>
                        <span>{q.query}</span>
                        <span style={{ color: '#6b7280', fontWeight: 600 }}>{q.value}</span>
                      </div>
                    ))}
                  </div>
                )}
                {trendsData.related_queries.rising?.length > 0 && (
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#dc2626', marginBottom: '0.4rem' }}>Rising Queries</div>
                    {trendsData.related_queries.rising.map((q, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem' }}>
                        <span>{q.query}</span>
                        <span style={{ color: '#dc2626', fontWeight: 600 }}>{q.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Keyword Comparison Tool */}
          <div className="card" style={{ padding: '1.2rem', marginBottom: '1rem', borderLeft: '4px solid #7c3aed' }}>
            <div style={{ fontWeight: 700, color: '#7c3aed', marginBottom: '0.5rem' }}>Compare Keywords (Live)</div>
            <p style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.75rem' }}>
              Compare up to 5 search terms side by side. Enter one keyword per line (e.g. your hotel name vs competitors).
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <textarea value={trendsCompareKws} onChange={e => setTrendsCompareKws(e.target.value)}
                placeholder={"banquet hall chennai\nwedding venue chennai\nconference hall chennai"} rows={3}
                style={{ ...inputStyle, flex: 1, fontFamily: 'monospace', fontSize: '0.82rem', resize: 'vertical' }} />
              <button onClick={compareTrends} disabled={trendsComparing}
                style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: 6, cursor: trendsComparing ? 'wait' : 'pointer',
                  fontWeight: 600, background: '#7c3aed', color: '#fff', whiteSpace: 'nowrap' }}>
                {trendsComparing ? 'Comparing...' : 'Compare'}
              </button>
            </div>

            {/* Comparison Results */}
            {trendsCompareData && trendsCompareData.series?.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', marginBottom: '0.5rem' }}>
                  Comparison ({trendsCompareData.data_points} data points)
                </div>
                {/* Summary: latest value per keyword */}
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                  {trendsCompareData.series.map((s, idx) => {
                    const colors = ['#059669', '#1e40af', '#d97706', '#dc2626', '#7c3aed'];
                    const latest = s.data[s.data.length - 1]?.value ?? 0;
                    return (
                      <div key={idx} style={{ padding: '0.4rem 0.8rem', borderRadius: 8, background: '#f8fafc', border: `2px solid ${colors[idx]}` }}>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{s.keyword}</div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: colors[idx] }}>{latest}</div>
                      </div>
                    );
                  })}
                </div>
                {/* Bar chart per keyword */}
                {trendsCompareData.series.map((s, idx) => {
                  const colors = ['#059669', '#1e40af', '#d97706', '#dc2626', '#7c3aed'];
                  const displayPts = s.data.slice(-26);
                  return (
                    <div key={idx} style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: colors[idx], marginBottom: 3 }}>{s.keyword}</div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 50 }}>
                        {displayPts.map((p, i) => (
                          <div key={i} title={`${p.date}: ${p.value}`}
                            style={{ flex: 1, minWidth: 3, maxWidth: 16, height: `${Math.max(p.value, 2)}%`,
                              background: colors[idx], borderRadius: '2px 2px 0 0', opacity: 0.8 }} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            Data source: Google Trends (public data, no API key required). Interest values are on a 0-100 scale relative to peak popularity.
            <span style={{ marginLeft: 8 }}>
              Bar colors: <span style={{ color: '#059669', fontWeight: 600 }}>High (70+)</span>{' '}
              <span style={{ color: '#d97706', fontWeight: 600 }}>Medium (40-69)</span>{' '}
              <span style={{ color: '#94a3b8', fontWeight: 600 }}>Low (&lt;40)</span>
            </span>
          </div>
        </>
      )}

      {/* ═══ SOCIAL MEDIA TAB ═══ */}
      {activeTab === 'social' && (
        <>
          {/* Actions bar */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <button onClick={syncAllSocial} disabled={socialSyncAllLoading}
              style={{ padding: '0.45rem 1rem', border: 'none', borderRadius: 6, cursor: socialSyncAllLoading ? 'wait' : 'pointer',
                fontWeight: 600, background: '#7c3aed', color: '#fff' }}>
              {socialSyncAllLoading ? 'Syncing All...' : 'Sync All Social Media'}
            </button>
            <select value={socialPlatformFilter} onChange={e => setSocialPlatformFilter(e.target.value)}
              style={{ padding: '0.4rem', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.82rem' }}>
              <option value="">All Platforms</option>
              <option value="INSTAGRAM">Instagram</option>
              <option value="FACEBOOK">Facebook</option>
            </select>
            <select value={socialCompFilter} onChange={e => setSocialCompFilter(e.target.value)}
              style={{ padding: '0.4rem', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.82rem' }}>
              <option value="">All Competitors</option>
              {competitors.filter(c => c.instagram_handle || c.facebook_page_url).map(c => (
                <option key={c.id} value={c.id}>{c.competitor_name}</option>
              ))}
            </select>
            <button onClick={loadSocialFeed} disabled={socialLoading}
              style={{ padding: '0.4rem 0.8rem', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', background: '#fff', fontWeight: 600, fontSize: '0.82rem' }}>
              Refresh
            </button>
            {socialFeed?.last_fetched && (
              <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: 'auto' }}>
                Last synced: {new Date(socialFeed.last_fetched).toLocaleString()}
              </span>
            )}
          </div>

          {socialLoading && <div style={{ color: '#6b7280', marginBottom: '0.75rem' }}>Loading social data...</div>}

          {/* Competitor Social Stats */}
          {socialFeed?.stats?.length > 0 && (
            <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 700, color: '#7c3aed', marginBottom: '0.75rem' }}>Competitor Social Overview</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr style={{ background: '#7c3aed', color: '#fff' }}>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Competitor</th>
                      <th style={{ padding: '0.5rem', textAlign: 'center' }}>IG Posts</th>
                      <th style={{ padding: '0.5rem', textAlign: 'center' }}>FB Posts</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right' }}>Total Likes</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right' }}>Comments</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right' }}>Shares</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right' }}>Avg Engagement %</th>
                      <th style={{ padding: '0.5rem', textAlign: 'center' }}>Sync</th>
                    </tr>
                  </thead>
                  <tbody>
                    {socialFeed.stats.map(s => (
                      <tr key={s.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.5rem', fontWeight: 600 }}>
                          {s.competitor_name}
                          <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 400 }}>
                            {s.instagram_handle && <span style={{ marginRight: 8 }}>IG: {s.instagram_handle}</span>}
                            {s.facebook_page_url && <span>FB</span>}
                          </div>
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          {s.ig_posts > 0 ? <span style={{ color: '#e1306c', fontWeight: 600 }}>{s.ig_posts}</span> : '-'}
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          {s.fb_posts > 0 ? <span style={{ color: '#1877f2', fontWeight: 600 }}>{s.fb_posts}</span> : '-'}
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>{s.total_likes > 0 ? s.total_likes.toLocaleString() : '-'}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>{s.total_comments > 0 ? s.total_comments.toLocaleString() : '-'}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>{s.total_shares > 0 ? s.total_shares.toLocaleString() : '-'}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                          {s.avg_engagement > 0 ? (
                            <span style={{ fontWeight: 600, color: s.avg_engagement >= 3 ? '#059669' : s.avg_engagement >= 1 ? '#d97706' : '#6b7280' }}>
                              {s.avg_engagement}%
                            </span>
                          ) : '-'}
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          <button onClick={() => syncSocial(s.id)} disabled={socialSyncing[s.id]}
                            style={{ padding: '0.2rem 0.5rem', border: '1px solid #c4b5fd', borderRadius: 4,
                              cursor: socialSyncing[s.id] ? 'wait' : 'pointer', fontSize: '0.72rem',
                              background: '#f5f3ff', color: '#7c3aed', fontWeight: 600 }}>
                            {socialSyncing[s.id] ? '...' : 'Sync'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Activity Feed */}
          {socialFeed?.feed?.length > 0 && (
            <div className="card" style={{ padding: '1rem' }}>
              <div style={{ fontWeight: 700, color: '#7c3aed', marginBottom: '0.75rem' }}>
                Activity Feed ({socialFeed.feed.length} posts)
              </div>
              <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {socialFeed.feed.map(post => (
                  <div key={post.id} style={{ padding: '0.75rem 0', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 3 }}>
                          <span style={{ fontSize: '0.72rem', padding: '1px 6px', borderRadius: 4, fontWeight: 700, color: '#fff',
                            background: post.platform === 'INSTAGRAM' ? '#e1306c' : '#1877f2' }}>
                            {post.platform === 'INSTAGRAM' ? 'IG' : 'FB'}
                          </span>
                          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{post.competitor_name}</span>
                          <span style={{ fontSize: '0.72rem', color: '#6b7280', padding: '1px 6px', borderRadius: 4, background: '#f3f4f6' }}>
                            {post.post_type}
                          </span>
                        </div>
                        {post.caption && (
                          <div style={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.4, marginBottom: 4,
                            maxHeight: 60, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {post.caption.length > 200 ? post.caption.slice(0, 200) + '...' : post.caption}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.78rem', color: '#6b7280' }}>
                          {post.likes > 0 && <span style={{ color: '#dc2626' }}>{post.likes.toLocaleString()} likes</span>}
                          {post.comments > 0 && <span>{post.comments.toLocaleString()} comments</span>}
                          {post.shares > 0 && <span>{post.shares.toLocaleString()} shares</span>}
                          {post.engagement_rate > 0 && (
                            <span style={{ fontWeight: 600, color: '#059669' }}>{post.engagement_rate}% eng.</span>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {post.post_date ? new Date(post.post_date).toLocaleDateString() : ''}
                        </div>
                        {post.post_url && (
                          <a href={post.post_url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: '0.72rem', color: '#1e40af' }}>View</a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {socialFeed && !socialFeed.feed?.length && !socialFeed.stats?.length && (
            <div className="card" style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
              No social media data yet. Add Instagram handles or Facebook URLs to competitors, configure API tokens in <strong>Intelligence Config</strong>, then click <strong>Sync All Social Media</strong>.
            </div>
          )}

          <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}>
            Requires Facebook App with Instagram Graph API access. Instagram uses Business Discovery API (competitor must be a Business/Creator account).
            Engagement % = (likes + comments + shares) / follower count.
          </div>
        </>
      )}

      {/* ═══ AI PREDICTIONS TAB ═══ */}
      {activeTab === 'ai-predictions' && (
        <>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
            <button onClick={loadPhqInsights} disabled={phqLoading}
              style={{ padding: '0.45rem 1rem', border: 'none', borderRadius: 6, cursor: phqLoading ? 'wait' : 'pointer',
                fontWeight: 600, background: '#7c3aed', color: '#fff' }}>
              {phqLoading ? 'Loading...' : 'Refresh Predictions'}
            </button>
          </div>

          {phqLoading && <div style={{ color: '#6b7280', marginBottom: '0.75rem' }}>Loading AI predictions...</div>}

          {/* Summary Cards */}
          {phqInsights?.summary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.6rem', marginBottom: '1rem' }}>
              {[
                { label: 'Total Events', value: phqInsights.summary.total_events, color: '#7c3aed' },
                { label: 'High Impact', value: phqInsights.summary.high_impact_events, color: '#dc2626' },
                { label: 'Medium Impact', value: phqInsights.summary.medium_impact_events, color: '#d97706' },
                { label: 'Predicted Attendance', value: (phqInsights.summary.total_predicted_attendance || 0).toLocaleString(), color: '#059669' },
                { label: 'Hotel Spend (USD)', value: `$${(phqInsights.summary.total_accommodation_spend_usd || 0).toLocaleString()}`, color: '#1e40af' },
                { label: 'F&B Spend (USD)', value: `$${(phqInsights.summary.total_hospitality_spend_usd || 0).toLocaleString()}`, color: '#9333ea' },
                ...(phqInsights.summary.nearby_events != null ? [{ label: `Nearby (≤${phqInsights.proximity_radius_km || 5}km)`, value: phqInsights.summary.nearby_events, color: '#059669' }] : []),
              ].map((c, i) => (
                <div key={i} style={{ padding: '0.6rem', background: '#fff', borderRadius: 8, textAlign: 'center', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 600, marginBottom: 2 }}>{c.label}</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 800, color: c.color }}>{c.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Events List */}
          {phqInsights?.events?.length > 0 ? (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {phqInsights.events.map((evt, i) => {
                const rank = evt.phq_rank || 0;
                const isExpanded = phqExpanded === evt.external_event_id;
                return (
                  <div key={evt.id} style={{ borderBottom: '1px solid #f3f4f6', borderLeft: evt.is_nearby ? '4px solid #059669' : 'none' }}>
                    <div onClick={() => setPhqExpanded(isExpanded ? null : evt.external_event_id)}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.65rem 0.75rem', cursor: 'pointer',
                        background: evt.is_nearby ? '#f0fdf4' : (i % 2 === 0 ? '#fff' : '#faf5ff') }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 800, fontSize: '0.85rem', color: '#fff', flexShrink: 0,
                        background: rank >= 70 ? '#dc2626' : rank >= 40 ? '#d97706' : '#6b7280' }}>
                        {rank}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{evt.title}</div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {evt.category} · {evt.city_name} · {evt.start_date}{evt.end_date && evt.end_date !== evt.start_date ? ` — ${evt.end_date}` : ''}
                          {evt.distance_km != null && (
                            <span style={{ marginLeft: 6, padding: '0px 5px', borderRadius: 3, fontSize: '0.68rem', fontWeight: 700,
                              background: evt.is_nearby ? '#dcfce7' : '#f3f4f6',
                              color: evt.is_nearby ? '#059669' : '#6b7280' }}>
                              {evt.distance_km} km{evt.is_nearby ? ' · NEARBY' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        {evt.phq_attendance ? <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#059669' }}>{Number(evt.phq_attendance).toLocaleString()} pax</div> : null}
                        {evt.predicted_spend_accommodation ? <div style={{ fontSize: '0.75rem', color: '#1e40af', fontWeight: 600 }}>${Number(evt.predicted_spend_accommodation).toLocaleString()} hotel</div> : null}
                        {evt.predicted_spend_hospitality ? <div style={{ fontSize: '0.75rem', color: '#9333ea', fontWeight: 600 }}>${Number(evt.predicted_spend_hospitality).toLocaleString()} F&B</div> : null}
                      </div>
                      <span style={{ fontSize: '0.8rem', color: '#94a3b8', flexShrink: 0 }}>{isExpanded ? '▲' : '▼'}</span>
                    </div>

                    {isExpanded && (
                      <div style={{ padding: '0.75rem', background: '#faf5ff', borderTop: '1px solid #ede9fe' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.6rem', fontSize: '0.8rem' }}>
                          {/* Spend Breakdown */}
                          <div style={{ background: '#fff', padding: '0.6rem', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                            <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: '0.3rem', fontSize: '0.78rem' }}>Predicted Spend (USD)</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total:</span><b>${Number(evt.predicted_spend_total || 0).toLocaleString()}</b></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Accommodation:</span><b style={{ color: '#1e40af' }}>${Number(evt.predicted_spend_accommodation || 0).toLocaleString()}</b></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>F&B / Hospitality:</span><b style={{ color: '#9333ea' }}>${Number(evt.predicted_spend_hospitality || 0).toLocaleString()}</b></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Transport:</span><b>${Number(evt.predicted_spend_transportation || 0).toLocaleString()}</b></div>
                          </div>

                          {/* Rankings */}
                          <div style={{ background: '#fff', padding: '0.6rem', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                            <div style={{ fontWeight: 700, color: '#7c3aed', marginBottom: '0.3rem', fontSize: '0.78rem' }}>AI Rankings</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Event Impact Rank:</span><b style={{ color: rank >= 70 ? '#dc2626' : rank >= 40 ? '#d97706' : '#6b7280' }}>{rank}/100</b></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Local Rank:</span><b>{evt.local_rank || '-'}/100</b></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Attendance:</span><b style={{ color: '#059669' }}>{evt.phq_attendance ? Number(evt.phq_attendance).toLocaleString() : '-'}</b></div>
                          </div>

                          {/* Location */}
                          <div style={{ background: '#fff', padding: '0.6rem', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                            <div style={{ fontWeight: 700, color: '#d97706', marginBottom: '0.3rem', fontSize: '0.78rem' }}>Location</div>
                            {evt.formatted_address && <div>{evt.formatted_address}</div>}
                            {evt.latitude && evt.longitude && <div style={{ color: '#6b7280', fontSize: '0.72rem' }}>Lat: {evt.latitude}, Lng: {evt.longitude}</div>}
                            <div>Country: <b>{evt.country_code || '-'}</b></div>
                            {evt.distance_km != null && (
                              <div style={{ marginTop: '0.2rem', fontWeight: 700, color: evt.is_nearby ? '#059669' : '#d97706' }}>
                                Distance from hotel: {evt.distance_km} km {evt.is_nearby ? '(NEARBY)' : ''}
                              </div>
                            )}
                          </div>

                          {/* Labels */}
                          {(Array.isArray(evt.phq_labels) ? evt.phq_labels : []).length > 0 && (
                            <div style={{ background: '#fff', padding: '0.6rem', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                              <div style={{ fontWeight: 700, color: '#059669', marginBottom: '0.3rem', fontSize: '0.78rem' }}>AI Labels</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                {(Array.isArray(evt.phq_labels) ? evt.phq_labels : []).map((lbl, li) => (
                                  <span key={li} style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 4, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>
                                    {lbl.label || lbl} {lbl.weight ? `(${(lbl.weight * 100).toFixed(0)}%)` : ''}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Impact Patterns */}
                          {(evt.impact_pattern_accommodation?.impacts || evt.impact_pattern_hospitality?.impacts) && (
                            <div style={{ background: '#fff', padding: '0.6rem', borderRadius: 6, border: '1px solid #e5e7eb', gridColumn: '1 / -1' }}>
                              <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: '0.3rem', fontSize: '0.78rem' }}>Demand Impact Patterns</div>
                              {evt.impact_pattern_accommodation?.impacts && (
                                <div style={{ marginBottom: '0.4rem' }}>
                                  <div style={{ fontWeight: 600, fontSize: '0.72rem', color: '#1e40af', marginBottom: '0.15rem' }}>Hotel/Accommodation demand surge:</div>
                                  <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                    {evt.impact_pattern_accommodation.impacts.map((imp, ii) => (
                                      <span key={ii} style={{ fontSize: '0.65rem', padding: '1px 5px', borderRadius: 3, fontWeight: 600,
                                        background: imp.position === 'event_day' ? '#fee2e2' : imp.position === 'leading' ? '#fef3c7' : '#f0fdf4',
                                        color: imp.position === 'event_day' ? '#991b1b' : imp.position === 'leading' ? '#92400e' : '#166534' }}>
                                        {imp.date_local?.slice(5)}: {imp.value > 0 ? '+' : ''}{imp.value} ({imp.position})
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {evt.impact_pattern_hospitality?.impacts && (
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: '0.72rem', color: '#9333ea', marginBottom: '0.15rem' }}>F&B/Banquet demand surge:</div>
                                  <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                    {evt.impact_pattern_hospitality.impacts.map((imp, ii) => (
                                      <span key={ii} style={{ fontSize: '0.65rem', padding: '1px 5px', borderRadius: 3, fontWeight: 600,
                                        background: imp.position === 'event_day' ? '#fce7f3' : imp.position === 'leading' ? '#fef3c7' : '#f0fdf4',
                                        color: imp.position === 'event_day' ? '#9d174d' : imp.position === 'leading' ? '#92400e' : '#166534' }}>
                                        {imp.date_local?.slice(5)}: {imp.value > 0 ? '+' : ''}{imp.value} ({imp.position})
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            !phqLoading && (
              <div className="card" style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                No AI event predictions available. Ask your admin to configure and sync the Event Calendar feed in Settings → Event Calendar APIs.
              </div>
            )
          )}

          <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}>
            Data source: AI/ML event predictions. Event Impact Rank (0-100) indicates global event impact. Spend predictions are in USD.
            Click any event to see full details including demand surge patterns.
          </div>
        </>
      )}
    </div>
  );
}

export default BanquetCompetitorMaster;
